import { useEffect } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Icon from '../Icons'
import API from '../../api/api'
import { setTicketData, setServiceFee, setValetSettings } from '../../reducers/parkingReducer'

import ParkingServicesSummary from './ParkingServicesSummary'

const ParkingServicesInfo = ({
  ticketData,
  serviceFee,
  setServiceFeeStore,
  valetSettings,
  setTicketDataStore,
  setValetSettingsStore,
}) => {
  const { t } = useTranslation(['parking', 'common'])
  const { ticketNumber } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    ;(async () => {
      // only fetch the ticket, if it's not in the store
      if (Object.keys(ticketData).length === 0) {
        try {
          const resp = await API.getParkingTicket(ticketNumber)
          setTicketDataStore(resp.data)
        } catch {
          navigate('/parking/error/404')
        }
      }
    })()
  }, [ticketData])

  useEffect(() => {
    ;(async () => {
      if (!serviceFee) {
        try {
          const resp = await API.calculateParkingFee(ticketNumber)
          setServiceFeeStore(resp.data.serviceFee)
        } catch (error) {
          console.error(error)
        }
      }
    })()
  }, [ticketNumber])

  useEffect(() => {
    ;(async () => {
      // only fetch the ticket, if it's not in the store
      if (Object.keys(valetSettings).length === 0 && ticketData.buildingId) {
        try {
          const resp = await API.getValetSettings(ticketData.buildingId)
          setValetSettingsStore(resp.data)
        } catch (error) {
          console.error(error)
        }
      }
    })()
  }, [ticketData.buildingId, valetSettings])

  const handleNext = async () => {
    if (!valetSettings.paymentRequired) {
      try {
        await API.requestVehicle(0, ticketNumber)
      } catch (error) {
        console.error(error)
      }
      return
    }

    if (ticketData.amountPaid === 0) {
      try {
        await API.prepayWithResidentBalance(ticketNumber)
      } catch (error) {
        console.error(error)
      }
    }

    // Note: Still show the payment page so guest can leave a tip
    navigate(`/parking/${ticketNumber}/payment`)
  }

  const ticketSummary = {
    startTime: ticketData.startDate,
    ticketNumber: ticketData.ticketNumber,
    cost: ticketData.serviceFee || 0,
    endTime: ticketData.endDate || new Date(),
    tip: ticketData.tip,
    amountPaid: ticketData.amountPaid,
    client: ticketData.clientId,
    clientName: ticketData.clientName,
    buildingId: ticketData.buildingId,
    parkingBalance: ticketData.parkingBalance,
    paid: ticketData.paid,
  }

  const renderButtonText = () => {
    if (ticketSummary.paid) {
      return t('parking:requestVehicle')
    }

    if (serviceFee) {
      if (valetSettings.paymentRequired) {
        return t('parking:pay')
      }
      return t('parking:requestVehicleAndPay')
    }
    return t('parking:requestVehicle')
  }

  return (
    <div className="parking-info">
      <div className="parking-info__back-button-container">
        <div>
          <Link to={`/parking/${ticketNumber}`}>
            <div className="parking-info__back-button">
              <Icon icon="arrow" />
              {t('common:actions.back')}
            </div>
          </Link>
        </div>
      </div>
      <ParkingServicesSummary ticketSummary={ticketSummary} />
      <button
        type="submit"
        data-testid="submit"
        className="button button-black parking-info__submit-button"
        onClick={handleNext}
      >
        {renderButtonText()}
      </button>
    </div>
  )
}

ParkingServicesInfo.propTypes = {
  ticketData: PropTypes.object,
  setTicketDataStore: PropTypes.func,
  setServiceFeeStore: PropTypes.func,
  serviceFee: PropTypes.number,
  valetSettings: PropTypes.object,
  setValetSettingsStore: PropTypes.func,
}

export default connect(
  (state) => ({
    ticketData: state.parking.ticketData,
    serviceFee: state.parking.serviceFee,
    valetSettings: state.parking.valetSettings,
  }),
  {
    setServiceFeeStore: setServiceFee,
    setTicketDataStore: setTicketData,
    setValetSettingsStore: setValetSettings,
  },
)(ParkingServicesInfo)
